import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { formatNumber } from '../../utils/numberFormat';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  progress?: {
    value: number;
    max: number;
  };
  icon?: React.ReactNode;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  progress,
  icon,
  color = 'primary.main',
}) => {

  const getProgressColor = () => {
    if (progress?.value == null || progress?.max == null) return 'primary';
    const percentage = (progress.value / progress.max) * 100;
    if (percentage < 10) return 'error';
    if (percentage < 30) return 'warning';
    return 'success';
  };

  return (
    <Card
      sx={{
        height: '100%',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${color}20`,
        borderRadius: 2,
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${color}20`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {icon && (
            <Box
              sx={{
                color,
                backgroundColor: `${color}15`,
                borderRadius: 1,
                p: 0.5,
                display: 'flex',
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {typeof value === 'number' ? formatNumber(value) : value}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}

        {trend && (
          <Box display="flex" alignItems="center" gap={0.5}>
            {trend.direction === 'up' ? (
              <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography
              variant="caption"
              color={trend.direction === 'up' ? 'success.main' : 'error.main'}
              sx={{ fontWeight: 500 }}
            >
              {Math.abs(trend.value)}%
            </Typography>
          </Box>
        )}

        {progress?.value != null && progress?.max != null && (
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                Used: {formatNumber(progress.value)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Limit: {formatNumber(progress.max)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(progress.value / progress.max) * 100}
              color={getProgressColor()}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {((progress.value / progress.max) * 100).toFixed(1)}% used
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatCard;
